import React, { forwardRef, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'

import CarouselLeftArrow from './carousel-left-arrow'
import CarouselRightArrow from './carousel-right-arrow'

import { makeStyles } from '@mui/styles'

const useStyles = makeStyles((theme) => ({
  swiper: {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  swiperWrapper: {
    position: 'relative',
    display: 'flex',
    transitionProperty: 'transform',
    boxSizing: 'content-box',
  },
  swiperSlide: {
    flexShrink: 0,
    width: '100%',
    height: '100%',
  },
  swiperButton: {
    zIndex: 5,
    cursor: 'pointer',
  },
  carouselDots: {
    display: 'flex !important',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    bottom: 25,
    width: '100%',
    zIndex: 2,

    '&.hide': {
      display: 'none !important',
    },

    // These replicate Swiper's default pagination styles:
    '& .swiper-pagination-bullet': {
      width: 12,
      height: 12,
      display: 'inline-block',
      borderRadius: '50%',
      background: '#ccc',
      margin: '0 4px',
      opacity: 0.7,
      transition: 'opacity 0.3s, background 0.3s',
      cursor: 'pointer',
    },
    '& .swiper-pagination-bullet-active': {
      background: theme.primary,
      opacity: 1,
      cursor: 'default',
    },
  },
}))

const Carousel = forwardRef((props, ref) => {
  const classes = useStyles()
  const [swiperInstance, setSwiperInstance] = useState(null)
  const [showArrows, setShowArrows] = useState(false)

  const {
    customClass,
    dotsClass,
    children,
    leftArrowClass,
    rightArrowClass,
    smallArrow,
    fillColor,
    settings = {},
    allowTextSelect = false,
    infinite = false,
    draggable = true,
    speed = 500,
    slidesToShow = 1,
    slidesToScroll = 1,
    dots = false,
    modules: additionalModules = [],
    thumbs,
  } = props

  const paginationId = useMemo(() => `carousel-pagination-${Math.random().toString(36).substr(2, 9)}`, [])

  const mergedSettings = {
    slidesPerView: slidesToShow,
    slidesPerGroup: slidesToScroll,
    speed,
    loop: infinite,
    pagination: dots ? { clickable: true, el: `#${paginationId}` } : false,
    navigation: false,
    allowTouchMove: draggable,
    // Swiper owns `pointerdown` on every slide, and its touch-start handler does two things that
    // break a dropdown living inside a slide: it calls preventDefault() (so no `mousedown` and no
    // focus ever follow), and — the one that survived the first fix — it calls
    // `document.activeElement.blur()` for any focused element matching `focusableElements`
    // (swiper-bundle.js, onTouchStart). react-select keeps focus on a hidden <input> (DummyInput)
    // while its menu is open, and that <input> matches Swiper's default list, so pressing the mouse
    // on an OPTION blurred it mid-click: react-select closed the menu on the blur, and the
    // `mousedown` that would have selected the option arrived after the option was already gone.
    // Measured in the expense edit view (2026-09-09): pointerdown → blur(relatedTarget: null) →
    // focusout → mousedown on an empty container. The menu opened, but nothing was ever selectable.
    //
    // `noSwipingSelector` is the right lever because Swiper checks it FIRST and returns before both
    // the blur and the preventDefault, and because it matches with closest() — so naming the menu
    // covers every option inside it, which a `focusableElements` entry would not (that one matches
    // the event target directly). Semantically it is also the true statement: pressing the mouse
    // inside a dropdown is not the start of a swipe. `.swiper-no-swiping` is Swiper's own default
    // and is kept, since setting this option replaces the class-based default rather than adding to it.
    noSwipingSelector: '.swiper-no-swiping, .react-select__control, .react-select__menu',
    ...settings,
  }

  useEffect(() => {
    if (!swiperInstance) return
    const totalSlides = swiperInstance.slides?.length || 0
    const visibleSlides = swiperInstance.params.slidesPerView || 1

    setShowArrows(totalSlides > visibleSlides)
  }, [swiperInstance, children, slidesToShow])

  return (
    <div className={clsx(classes.swiperWrapper, customClass)}>
      {showArrows && (
        <>
          <div className={clsx(classes.swiperButton, 'swiper-button-prev', leftArrowClass)}>
            <CarouselLeftArrow
              smallArrow={smallArrow}
              fillColor={fillColor}
              onClick={() => swiperInstance?.slidePrev()}
            />
          </div>
          <div className={clsx(classes.swiperButton, 'swiper-button-next', rightArrowClass)}>
            <CarouselRightArrow
              smallArrow={smallArrow}
              fillColor={fillColor}
              onClick={() => swiperInstance?.slideNext()}
            />
          </div>
        </>
      )}

      <Swiper
        onSwiper={setSwiperInstance}
        ref={ref}
        modules={[Navigation, Pagination, ...additionalModules]}
        className={clsx(classes.swiper, allowTextSelect && 'carousel-text-select')}
        wrapperClass={classes.swiperWrapper}
        slideClass={classes.swiperSlide}
        thumbs={thumbs}
        {...mergedSettings}
      >
        {React.Children.map(children, (child, index) => (
          <SwiperSlide key={index} className={classes.swiperSlide}>
            {child}
          </SwiperSlide>
        ))}
      </Swiper>

      <div id={paginationId} className={clsx(classes.carouselDots, { hide: !showArrows }, dotsClass)} />
    </div>
  )
})

Carousel.propTypes = {
  customClass: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  leftArrowClass: PropTypes.string,
  rightArrowClass: PropTypes.string,
  fillColor: PropTypes.string,
  smallArrow: PropTypes.bool,
  settings: PropTypes.object,
  allowTextSelect: PropTypes.bool,
  // Slick-style compatibility
  dots: PropTypes.bool,
  infinite: PropTypes.bool,
  draggable: PropTypes.bool,
  speed: PropTypes.number,
  slidesToShow: PropTypes.number,
  slidesToScroll: PropTypes.number,
  dotsClass: PropTypes.string,

  // Swiper-specific
  modules: PropTypes.array,
  thumbs: PropTypes.object,
}

export default Carousel
